/**
 * The cart's discount ladder and free shipping bar in the shopper's currency, at the rate Shopify
 * is pricing the page with.
 *
 * Liquid has no exchange rate, so the theme used to carry one per currency by hand: nineteen
 * `cart_level_multiplier_*` section settings in `snippets/c-cart-level.liquid` (with a 1:1
 * fallback for every other currency the store sells in) and nineteen `free_shipping_*` theme
 * settings in `snippets/c-shipping-bar.liquid`. On 2026-10-24 a ₱4,600 cart read "You unlocked
 * 20% off." with chips "₱55.00 → 10%", "₱75.00 → 15%" and "₱100.00 → 20%" beside the -₱460 (10%)
 * discount checkout applied: the US dollar tiers, printed as pesos. On 2026-10-29 an A$41 cart
 * read "Congrats! You've unlocked FREE SHIPPING!" (free_shipping_aud was 40) while checkout
 * charged it US$10: the live quotes were A$41 charged and A$43 free, ¥4,500 charged and ¥4,800
 * free, £21 charged and £23 free, €23.85 charged and €27.85 free.
 *
 * The page already carries the live rate: Shopify writes `Shopify.currency = { active, rate }`
 * into `content_for_header` on every storefront page (64.88628 for PHP that day). Liquid now
 * renders the amounts in the store's base currency (US dollars: `amount_usd` on each `cart_level`
 * block, `free_shipping_usd` in theme settings) and this module converts them. That is the rate
 * the admin's "Cart discount level 1/2/3" automatic discounts and the US$30 shipping rate are
 * converted at, so at ₱64.88628 the tiers are ₱3,569, ₱4,867 and ₱6,489 and the ₱4,600 cart
 * reads 10%, as checkout does, and at A$1.45146 the bar counts an A$41 cart up to A$44.
 *
 * Ported from WOOPF-Shopify's `assets/woopf-cart-thresholds.js` (c2cf7fc, GAP1-05 and GAP1-11).
 * The ladder half came on 2026-10-24. The shipping half waited for R25 (design-foundation spec 5.6
 * item 8): the bar's threshold per currency had to be confirmed first. The live checkout quotes of
 * 2026-10-29 are that truth table (merchant-listings spec section 11): one rule, free from US$30
 * at Shopify's rate in every currency, so the `shipping` renderer was ported on 2026-10-30.
 *
 * ROUNDING. A converted threshold is rounded up to a whole unit of the shopper's currency, so the
 * cart can be a few cents late to announce a discount or free shipping but never early. The bar
 * must never say free where checkout charges.
 *
 * FAIL SAFE. Each element renders with `style="display: none;"` and is shown only once there is a
 * rate and a money format to print with. No rate, no ladder and no bar.
 *
 * BASE CURRENCY. The amounts are in the store's base currency, US dollars today. If the base
 * currency changes, the `cart_level` blocks of the drawer and the cart page and `free_shipping_usd`
 * must be re-entered in the new base currency the same day.
 *
 * NO GLOBALS. Everything is reached through an injected `scope`: the window in the browser, a
 * plain object in the tests (`tests/assets/purrz-cart-thresholds.test.mjs`).
 */

/** @param {{ Shopify?: { currency?: { rate?: unknown } } } | undefined} scope */
export const readRate = (scope) => {
  const rate = Number.parseFloat(String(scope?.Shopify?.currency?.rate ?? ''));
  return Number.isFinite(rate) && rate > 0 ? rate : null;
};

/**
 * Base currency cents to shopper currency cents, rounded up to a whole unit. `toFixed(6)` comes
 * first because 100 * 1.1 is 110.00000000000001 in floating point, and a bare ceil would say 111.
 *
 * @param {number | string} baseCents
 * @param {number | null} rate
 * @returns {number | null}
 */
export const convertUp = (baseCents, rate) => {
  const cents = Number(baseCents);
  if (!Number.isFinite(cents) || cents <= 0 || !(rate > 0)) return null;
  const major = Number(((cents / 100) * rate).toFixed(6));
  return Math.ceil(major) * 100;
};

/** 111222333 cents is 1,112,223 whole units: seven integer digits, then the fraction. */
export const MONEY_SAMPLE_CENTS = 111222333;

/**
 * Read the money format off one sentinel amount rendered by Liquid's own `money` filter.
 * `shop.money_format` is not usable: it is the store's US dollar format on every market, while the
 * drawer renders pesos, yen and euros.
 *
 * @param {string | undefined} text e.g. "$1,112,223.33", "€1.112.223,33", "¥1,112,223"
 * @returns {{ prefix: string, suffix: string, group: string, decimal: string, decimals: number } | null}
 */
export const parseMoneySample = (text) => {
  const sample = String(text ?? '');
  const first = sample.search(/\d/);
  if (first < 0) return null;

  let last = sample.length - 1;
  while (last >= 0 && !/\d/.test(sample[last])) last -= 1;

  const core = sample.slice(first, last + 1);
  const decimals = core.replace(/\D/g, '').length - 7;
  if (decimals < 0 || decimals > 3) return null;

  const integerPart = decimals > 0 ? core.slice(0, core.length - decimals - 1) : core;
  const group = integerPart.match(/\D/);

  return {
    prefix: sample.slice(0, first),
    suffix: sample.slice(last + 1),
    group: group ? group[0] : '',
    decimal: decimals > 0 ? core.charAt(core.length - decimals - 1) : '',
    decimals,
  };
};

/**
 * @param {number} cents
 * @param {ReturnType<typeof parseMoneySample>} format
 * @returns {string} A zero fraction is dropped ("₱267", not "₱267.00"), as the product pages print.
 */
export const formatMoney = (cents, format) => {
  const amount = Number(cents);
  if (!format || !Number.isFinite(amount)) return '';

  const [whole, fraction = ''] = (amount / 100).toFixed(format.decimals).split('.');
  const grouped = format.group ? whole.replace(/\B(?=(\d{3})+(?!\d))/g, format.group) : whole;
  const body =
    fraction && Number(fraction) !== 0 ? `${grouped}${format.decimal}${fraction}` : grouped;

  return `${format.prefix}${body}${format.suffix}`;
};

/**
 * @template {{ cents: number, discount: string }} Tier
 * @param {Tier[]} tiers Thresholds already in shopper currency cents, in any order.
 * @param {number} cartCents
 */
export const ladderState = (tiers, cartCents) => {
  const sorted = tiers.filter((tier) => tier.cents > 0).sort((a, b) => a.cents - b.cents);
  if (sorted.length === 0 || !Number.isFinite(cartCents)) return null;

  const reached = sorted.filter((tier) => cartCents >= tier.cents);
  const next = sorted.find((tier) => cartCents < tier.cents) ?? null;
  const highest = sorted[sorted.length - 1].cents;

  return {
    current: reached.length > 0 ? reached[reached.length - 1] : null,
    next,
    remainingCents: next ? next.cents - cartCents : 0,
    progress: Math.min(100, Math.floor((cartCents * 100) / highest)),
    steps: sorted.map((tier) => ({ ...tier, active: cartCents >= tier.cents })),
  };
};

/**
 * @param {number | null} thresholdCents In shopper currency cents.
 * @param {number} cartCents
 */
export const shippingState = (thresholdCents, cartCents) => {
  if (!(thresholdCents > 0) || !Number.isFinite(cartCents)) return null;
  return {
    unlocked: cartCents >= thresholdCents,
    remainingCents: Math.max(0, thresholdCents - cartCents),
  };
};

export const ELEMENT_NAME = 'pz-cart-threshold';

/** The amount is built from digits and the money sample, but the sample is merchant data. */
const escapeHtml = (text) =>
  String(text).replace(/[&<>"']/g, (character) => `&#${character.charCodeAt(0)};`);

/**
 * @param {any} element A <pz-cart-threshold data-pz-kind="ladder"> from snippets/c-cart-level.liquid.
 * @param {number} rate
 * @param {NonNullable<ReturnType<typeof parseMoneySample>>} format
 */
export const renderLadder = (element, rate, format) => {
  const tiers = Array.from(element.querySelectorAll('[data-pz-step]'), (node) => ({
    node,
    cents: convertUp(node.dataset.pzBaseCents, rate),
    discount: String(node.dataset.pzDiscount ?? ''),
  })).filter((tier) => tier.cents !== null);
  const state = ladderState(tiers, Number(element.dataset.pzCartCents));
  const message = element.querySelector('[data-pz-message]');
  const fill = element.querySelector('[data-pz-fill]');
  if (!state || !message || !fill) return false;

  message.textContent = state.next
    ? String(element.dataset.pzProgressText ?? '')
        .replaceAll('[amount]', () => formatMoney(state.remainingCents, format))
        .replaceAll('[discount]', () => state.next.discount)
    : String(element.dataset.pzSuccessText ?? '').replaceAll(
        '[discount]',
        () => state.current.discount,
      );
  fill.style.width = `${state.progress}%`;

  for (const step of state.steps) {
    step.node.textContent = `${formatMoney(step.cents, format)} → ${step.discount}%`;
    step.node.classList.toggle('is-active', step.active);
  }
  return true;
};

/**
 * @param {any} element A <pz-cart-threshold data-pz-kind="shipping"> from snippets/c-shipping-bar.liquid.
 * @param {number} rate
 * @param {NonNullable<ReturnType<typeof parseMoneySample>>} format
 */
export const renderShipping = (element, rate, format) => {
  // A fixed threshold in the shopper's own currency (data-pz-local-cents, Australia's A$44 since
  // 2026-10-30) is what checkout charges by, so it is used as it is, never converted.
  const local = Number(element.dataset.pzLocalCents);
  const state = shippingState(
    local > 0 ? local : convertUp(element.dataset.pzBaseCents, rate),
    Number(element.dataset.pzCartCents),
  );
  const bar = element.querySelector('[data-pz-bar]');
  const message = element.querySelector('[data-pz-message]');
  if (!state || !bar || !message) return false;

  bar.classList.toggle('is-active', state.unlocked);
  // The two templates are locale strings rendered by Liquid; only the amount is ours to escape.
  message.innerHTML = state.unlocked
    ? String(element.dataset.pzSuccessHtml ?? '')
    : String(element.dataset.pzProgressHtml ?? '').replaceAll('[amount]', () =>
        escapeHtml(formatMoney(state.remainingCents, format)),
      );
  return true;
};

const RENDERERS = new Map([
  ['ladder', renderLadder],
  ['shipping', renderShipping],
]);

/** @param {any} element @param {any} scope */
export const renderThreshold = (element, scope) => {
  const data = element?.dataset ?? {};
  const rate = readRate(scope);
  const format = parseMoneySample(data.pzMoneySample);
  const render = RENDERERS.get(data.pzKind);
  if (rate === null || format === null || !render) return false;
  if (!render(element, rate, format)) return false;
  element.style.display = 'block';
  return true;
};

/** @param {any} scope The window in the browser. */
export const defineThresholdElement = (scope) => {
  const registry = scope?.customElements;
  if (!registry || typeof scope.HTMLElement !== 'function' || registry.get(ELEMENT_NAME)) {
    return false;
  }
  registry.define(
    ELEMENT_NAME,
    class extends scope.HTMLElement {
      connectedCallback() {
        renderThreshold(this, scope);
      }
    },
  );
  return true;
};

/*
 * The auto-start. `typeof window` guards the Vitest run, which has no DOM at all. A module script
 * is deferred, so this runs after content_for_header has written window.Shopify.currency.
 */
if (typeof window !== 'undefined') {
  defineThresholdElement(window);
}
